'use client';

import { useEffect, useRef, useState } from 'react';
import { UGC_PAGE_PATH, type UgcCableSystem, type UgcPhotoCard, type UgcProduct } from 'helpers/ugc';
import { productFilterSearch, ugcProductLabel } from 'helpers/ugc-filters';
import CtaChevron from './cta-chevron';
import { typeScale } from './type-scale';
import { Lightbox, packRows, UgcAuthenticNote, UgcRows, UgcRowsPlaceholder, useIsDesktop } from './ugc-rows';

// Fewer photos than this fit into full rows and the section hides: a thin strip reads worse than none.
const MIN_PHOTOS = 5;
const FETCH_LIMIT = 30;
// Row width in units of row height; higher = more, shorter photos per row.
// Split sits beside the PDP copy column, so its rows pack more, smaller photos than the photos'
// natural size would suggest — two huge tiles per row read as a banner, not a gallery.
const ROW_TARGET = { split: { mobile: 2, desktop: 3.8 }, wide: { mobile: 2, desktop: 4.5 } };
const ROW_COUNT = { split: 2, wide: 3 };

interface UgcGalleryProps {
  /** Filter to one product; omit for every photo (home page). */
  product?: UgcProduct;
  /** Narrow cable railing to one system (Signature / Express / DriveTite PDPs). */
  system?: UgcCableSystem;
  /**
   * `split` (PDP, after Figma 44:1583): copy column left, two rows of photos right.
   * `wide` (home / category landings): heading row above, three full-width rows.
   */
  variant?: 'split' | 'wide';
  /**
   * What the heading names — "See real {subject} projects". Defaults to the product's label
   * ("Cable Railing"), or "customer" with no product (home page). PDPs pass their subcategory.
   */
  subject?: string;
  className?: string;
}

// A short preview of customer project photos. "See more" continues on /customer-projects (same
// filter), which is where the curated viewrail.com gallery and the photo upload are linked from. Nothing is fetched until
// the section is within ~1500px (about two screens) of the viewport, so the photo list is usually back
// before the section scrolls into view; every image is lazy.
const UgcGallery = ({ product, system, variant = 'split', subject, className = '' }: UgcGalleryProps) => {
  const title = `See real ${subject ?? (product ? ugcProductLabel(product) : undefined) ?? 'customer'} projects`;
  const ref = useRef<HTMLElement>(null);
  const [photos, setPhotos] = useState<UgcPhotoCard[] | null>(null);
  const [total, setTotal] = useState(0);
  // The PDP section grows in place: "Load more" adds rows (fetching another batch when the ones in
  // hand run low), so a shopper can browse more installs without leaving the product.
  const [maxRows, setMaxRows] = useState(ROW_COUNT[variant]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const isDesktop = useIsDesktop();

  // Nothing is fetched until the section is near the viewport.
  const [nearViewport, setNearViewport] = useState(false);
  // A failed load (503 while Cloudinary is down, or a network error) is kept apart from a genuine
  // empty result: only an empty 200 hides the section; a failure shows a retry.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        setNearViewport(true);
      },
      { rootMargin: '1500px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!nearViewport) return;
    let cancelled = false;
    const qs = new URLSearchParams({ limit: String(FETCH_LIMIT), ...(product ? { product } : {}), ...(system ? { system } : {}) });
    fetch(`/api/ugc/?${qs}`)
      .then((r) => {
        if (!r.ok) throw new Error(`UGC photos ${r.status}`);
        return r.json() as Promise<{ photos: UgcPhotoCard[]; total: number }>;
      })
      .then((d) => {
        if (cancelled) return;
        setPhotos(d.photos);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [nearViewport, product, system, attempt]);

  const retry = () => {
    setFailed(false);
    setAttempt((n) => n + 1);
  };

  const target = ROW_TARGET[variant][isDesktop ? 'desktop' : 'mobile'];
  const rows = photos ? packRows(photos, target, { maxRows, keepTrailing: photos.length >= total }) : null;
  // Rows take photos in order from the start, so the shown photos are a prefix of the list and a
  // tile's index works directly in the lightbox.
  const shown = photos?.slice(0, rows?.flat().length) ?? [];
  if (rows && shown.length < MIN_PHOTOS) return null;
  const canLoadMore = variant === 'split' && shown.length < total;

  const loadMore = async () => {
    if (!photos) return;
    setLoadingMore(true);
    // Fetch the next batch only when the photos not yet placed won't fill two more rows.
    if (photos.length - shown.length < FETCH_LIMIT / 2 && photos.length < total) {
      const qs = new URLSearchParams({ limit: String(FETCH_LIMIT), offset: String(photos.length), ...(product ? { product } : {}), ...(system ? { system } : {}) });
      try {
        const res = await fetch(`/api/ugc/?${qs}`);
        if (res.ok) {
          const next: { photos: UgcPhotoCard[] } = await res.json();
          setPhotos((current) => [...(current ?? []), ...next.photos]);
        }
      } catch (error) {
        console.error('[ugc-gallery] could not load more photos:', error);
      }
    }
    setMaxRows((n) => n + ROW_COUNT[variant]);
    setLoadingMore(false);
  };

  const morePath = product ? `${UGC_PAGE_PATH}?${productFilterSearch(product, system)}` : UGC_PAGE_PATH;
  // On a PDP the link opens a new tab, so following it doesn't take the shopper off the product.
  const seeMore = (
    <a
      href={morePath}
      {...(variant === 'split' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="inline-flex min-h-44 items-center font-gt-s text-14 font-bold text-[#212121] hover:underline"
    >
      See more customer projects
      <CtaChevron />
    </a>
  );
  const intro = 'Real installs, photographed by Viewrail customers.';

  return (
    // Split uses the PDP's `.container` so the section lines up with (and is no wider than) the
    // product content above it; wide spans the landing page like the sections around it.
    <section
      ref={ref}
      aria-label="Customer project photos"
      className={`${variant === 'split' ? 'container' : 'w-full px-24 md:px-38'} py-64 font-gt text-[#212121] lg:py-96 ${className}`}
    >
      <div className={variant === 'split' ? 'flex flex-col gap-32 lg:flex-row lg:gap-48' : 'flex flex-col gap-24'}>
        {variant === 'split' ? (
          // PDP: a narrow copy column beside the photos, with room for the full authenticity badge.
          <div className="flex shrink-0 flex-col gap-16 lg:w-280">
            <h2 className={typeScale.h4}>{title}</h2>
            <p className={typeScale.body}>{intro}</p>
            <UgcAuthenticNote />
          </div>
        ) : (
          // Landings: one compact header — heading, a line of intro and the one-line authenticity
          // note — so the photos lead.
          <div className="flex flex-col gap-8">
            <h2 className={typeScale.h2}>{title}</h2>
            <p className={typeScale.body}>{intro}</p>
            <UgcAuthenticNote variant="inline" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          {rows ? (
            <UgcRows rows={rows} areaVw={variant === 'split' ? 65 : 95} onOpen={setActive} />
          ) : (
            failed ? (
              <div className="flex flex-col items-start gap-12 bg-[#f2f2f2] p-24" role="status">
                <p className={typeScale.body}>Customer photos couldn&apos;t load just now.</p>
                <button type="button" onClick={retry} className={typeScale.button}>
                  Try again
                </button>
              </div>
            ) : (
              <UgcRowsPlaceholder count={ROW_COUNT[variant]} target={target} />
            )
          )}
          {/* Under the photos: the natural next step once someone has looked through them. */}
          <div className="mt-16 flex flex-wrap items-center gap-x-32 gap-y-8">
            {canLoadMore && (
              <button type="button" onClick={loadMore} disabled={loadingMore} className={`${typeScale.button} disabled:opacity-60`}>
                {loadingMore ? 'Loading…' : 'Load more photos'}
              </button>
            )}
            {seeMore}
          </div>
        </div>
      </div>

      {active !== null && (
        <Lightbox
          photos={shown}
          index={active}
          onChange={setActive}
          footer={
            <a href={morePath} className="mt-8 font-gt-s text-14 font-bold text-white underline">
              See all customer projects
            </a>
          }
        />
      )}
    </section>
  );
};

export default UgcGallery;
